import { z } from 'zod'

/**
 * ISO 8601 datetime string that must be in UTC (end with `Z`).
 */
export const utcDateTimeString = z.string().datetime({ offset: false })

/**
 * HH:MM time string (00:00–23:59).
 */
export const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be HH:MM')

/**
 * Cross-field refinement: ensures `startTime < endTime` when both are
 * present and parseable. Apply with `.superRefine(dateRangeRefinement)` on any
 * object schema that contains `startTime` and `endTime` string fields.
 */
export function dateRangeRefinement(
  data: { startTime?: string; endTime?: string },
  ctx: z.RefinementCtx,
): void {
  if (!data.startTime || !data.endTime) return
  const start = Date.parse(data.startTime)
  const end = Date.parse(data.endTime)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return
  if (start >= end) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endTime'],
      message: 'endTime must be after startTime',
    })
  }
}

/**
 * Flags any occurrence whose start instant duplicates an earlier one. The
 * `event_occurrences` table is unique on `(eventId, startTime)`; comparing by
 * parsed instant (not raw string) rejects the same moment written with different
 * timezone offsets, so the DB constraint can never be reached from valid input.
 */
export function occurrenceStartsUniqueRefinement(
  occurrences: { startTime: string }[] | undefined,
  ctx: z.RefinementCtx,
): void {
  if (!occurrences) return
  const seen = new Set<number>()
  occurrences.forEach((occurrence, index) => {
    const instant = Date.parse(occurrence.startTime)
    if (Number.isNaN(instant)) return
    if (seen.has(instant)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['occurrences', index, 'startTime'],
        message: 'Each occurrence must have a unique start time',
      })
    }
    seen.add(instant)
  })
}
